/**
 * Matrix Templates Hook
 *
 * Manages CRUD operations for matrix/simple filter templates.
 * Handles both 2D (matrix) and 1D (simple) filter types.
 *
 * @param {Object} options
 * @param {Object} options.activeTemplate - Currently selected template
 * @param {Function} options.setActiveTemplate - Template setter
 * @param {Function} options.setFilter - Filter state setter
 * @param {Function} options.setShowSaveModal - Modal visibility setter
 * @param {number} options.hexagonSize - Current hexagon size in m²
 * @param {Object} options.filter - Current filter configuration
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	getFilterTemplates,
	createFilterTemplate,
	updateFilterTemplate,
	deleteFilterTemplate,
	parseFilterTemplate,
} from '@utils/api';
import { useToast } from '@contexts/Toast.context';

export default function useMatrixTemplates({
	activeTemplate,
	setActiveTemplate,
	setFilter,
	setShowSaveModal,
	hexagonSize,
	filter,
}) {
	const { addToast } = useToast();
	const queryClient = useQueryClient();

	// Fetch saved matrix templates
	const { data: templatesData, refetch: refetchTemplates } = useQuery({
		queryKey: ['filterTemplates'],
		queryFn: () => getFilterTemplates({ limit: 100 }),
	});

	// Filter to show matrix/simple templates (filter_mode is 'matrix_filter' or 'simple_filter')
	const matrixTemplates = useMemo(() => {
		if (!templatesData?.filter_templates) return [];
		return templatesData.filter_templates
			.filter(t => t.filter_mode === 'matrix_filter' || t.filter_mode === 'simple_filter')
			.map(parseFilterTemplate);
	}, [templatesData]);

	// Save template mutation
	const saveMutation = useMutation({
		mutationFn: data => {
			// Pass filterType='matrix' to the API layer which then determines
			// the correct filter_mode ('simple_filter' or 'matrix_filter')
			// based on matrixConfig.type.
			const filterType = 'matrix';
			if (activeTemplate?.uuid) {
				return updateFilterTemplate({
					uuid: activeTemplate.uuid,
					title: data.title,
					hexagonSize,
					filterType,
					matrixConfig: filter,
				});
			}
			return createFilterTemplate({
				title: data.title,
				hexagonSize,
				filterType,
				matrixConfig: filter,
			});
		},
		onSuccess: res => {
			addToast({ title: 'Template saved', bg: 'success' });
			setShowSaveModal(false);

			const parsed = parseFilterTemplate(res);
			setActiveTemplate(parsed);
			queryClient.invalidateQueries({ queryKey: ['filterTemplates'] });
		},
		onError: err => {
			addToast({ title: 'Could not save template', bg: 'danger' });
			console.error(err);
		},
	});

	// Delete template mutation
	const deleteMutation = useMutation({
		mutationFn: uuid => deleteFilterTemplate(uuid),
		onSuccess: () => {
			addToast({ title: 'Template deleted', bg: 'success' });
			setActiveTemplate(null);
			queryClient.invalidateQueries({ queryKey: ['filterTemplates'] });
		},
		onError: () => {
			addToast({ title: 'Could not delete template', bg: 'danger' });
		},
	});

	/**
	 * Load a template into the filter state
	 * Handles both matrix (2D) and simple (1D) filter types
	 * @param {Object} template - Parsed template object
	 */
	const loadTemplate = template => {
		if (!template?.matrixConfig) return;

		const config = template.matrixConfig;

		// Build filter state from template, preserving the filter type
		setFilter({
			...config,
			name: template.title,
			type: config.type || 'matrix',
			currency: config.currency || 'EUR',
		});
		setActiveTemplate(template);
	};

	/**
	 * Validate and save template.
	 * Validates filter configuration before triggering the save mutation.
	 * Shows a toast with specific error if validation fails.
	 * @param {Object} data - Save data (e.g. { title })
	 */
	const saveTemplate = data => {
		if (filter.type === 'simple' && filter.classes?.length) {
			const invalidClasses = filter.classes.filter(
				cls => cls.max !== null && cls.max !== undefined && cls.min > cls.max
			);
			if (invalidClasses.length > 0) {
				const labels = invalidClasses.map(c => c.label).join(', ');
				addToast({
					title: `Invalid range: min > max for: ${labels}`,
					bg: 'danger',
				});
				return;
			}
		}
		// Validate custom price field labels are unique (API requirement)
		const customLabels = (filter.customPriceFields || []).map(f => f.label);
		if (new Set(customLabels).size !== customLabels.length) {
			addToast({ title: 'Custom price labels must be unique', bg: 'danger' });
			return;
		}
		saveMutation.mutate(data);
	};

	return {
		matrixTemplates,
		saveMutation,
		saveTemplate,
		deleteMutation,
		loadTemplate,
		refetchTemplates,
	};
}
